import { Box, CircularProgress, MenuItem, Select, Typography } from '@mui/material';
import {
  MultiselectPollQuestionReport,
  RangePollQuestionReport,
  SelectPollQuestionReport,
  TextPollQuestionReport,
} from './QuestionReport';
import { Poll, PollResponse, PollTemplate, ReportData, ReportDataMap, ResponseValue, TextQnAs, User } from '../../types';
import React, { useEffect, useState } from 'react';
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';

import { db } from '../../firebase';
import useAuth from '../../hooks/useAuth';

const SurveyReport: React.FC = () => {
  const { user, loading } = useAuth() as { user: User | null, loading: boolean };
  
  const [error, setError] = useState('');
  const [polls, setPolls] = useState<Poll[]>([]);
  const [selectedPoll, setSelectedPoll] = useState<Poll | null>(null);
  const [pollTemplate, setPollTemplate] = useState<PollTemplate | null>(null);
  const [reportDataMap, setReportDataMap] = useState<ReportDataMap | null>(null);
  const [textQnAs, setTextQnAs] = useState<TextQnAs | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [responseCount, setResponseCount] = useState(0);

  const tallySelectResponse = (reportData: ReportData, answer: ResponseValue): ReportData => {
    const a = answer as string;
    if (a in reportData.data) {
      reportData.data[a]++;
    } else {
      reportData.data[a] = 1;
    }
    return reportData;
  };

  const tallyMultiselectResponse = (reportData: ReportData, answer: ResponseValue): ReportData => {
    const a = answer as string[];
    if (a.length > 0) {
      a.forEach((answer) => {
        if (answer in reportData.data) {
          reportData.data[answer]++;
        } else {
          reportData.data[answer] = 1;
        }
      });
    }
    return reportData;
  }

  const tallyRangeResponse = (reportData: ReportData, answer: ResponseValue): ReportData => {
    if (!("min" in reportData.data)) {
      reportData.data.min = answer as number;
    }
    if (!("max" in reportData.data)) {
      reportData.data.max = answer as number;
    }
    if (!("count" in reportData.data)) {
      reportData.data.count = 0;
    }
    if (!("sum" in reportData.data)) {
      reportData.data.sum = 0;
    }

    const a = answer as number;
    if (a > reportData.data.max) {
      reportData.data.max = a;
    }
    if (a < reportData.data.min) {
      reportData.data.min = a;
    }
    reportData.data.count++;
    reportData.data.sum += a;
    return reportData;
  };

  const collectTextResponses = (txtQnAs: TextQnAs, responseId: string, question: string, answer: ResponseValue): TextQnAs => {
    txtQnAs.questions.add(question);

    if (!(responseId in txtQnAs.answers)) {
      txtQnAs.answers[responseId] = {};
    }

    txtQnAs.answers[responseId][question] = answer;
    
    return txtQnAs;
  };

  const checkResponses = async (selectedPoll: Poll, pollTemplate: PollTemplate) => {
    const rprtDataMap: ReportDataMap = {};

    pollTemplate.questions.forEach((question) => {
      rprtDataMap[question.text] = {
        question: question,
        data: {},
      }
    });

    let _textQnAs: TextQnAs = {
      questions: new Set<string>(),
      answers: {}
    }
    
    const responsesCollection = collection(db, 'pollResponses');
    const q = query(responsesCollection, where('pollId', '==', selectedPoll.id));
    const querySnapshot = await getDocs(q);

    const responses: PollResponse[] = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data() as PollResponse;
      if (!data.voided) {
        responses.push(data);
      }
    });

    responses.forEach((data) => {
      Object.entries(data.responses).forEach(([q, a]) => {
        const reportData = rprtDataMap[q];
        if (reportData) {
          switch (reportData.question.type) {
            case "select":
              rprtDataMap[q] = tallySelectResponse(reportData, a);
              break;
            case "multiselect":
              rprtDataMap[q] = tallyMultiselectResponse(reportData, a);
              break;
            case "range":
              rprtDataMap[q] = tallyRangeResponse(reportData, a);
              break;
            case "text":
              _textQnAs = collectTextResponses(_textQnAs, data.id, q, a);
              break;
          }
        }
      });
    });

    setResponseCount(responses.length);
    setReportDataMap(rprtDataMap);
    setTextQnAs(_textQnAs);
  };

  useEffect(() => {
    if (selectedPoll && pollTemplate) {
      setIsAnalyzing(true);
      setError("");
      checkResponses(selectedPoll, pollTemplate).then(() => {
        setIsAnalyzing(false);
      })
    }
  }, [pollTemplate]);

  useEffect(() => {
    const fetchPollTemplate = async (pollId: string) => {
      try {
        const pollRef = doc(db, 'polls', pollId);
        const pollSnap = await getDoc(pollRef);
        
        if (pollSnap.exists()) {
          setPollTemplate(pollSnap.data() as PollTemplate);
        } else {
          setError('Poll template not found');
        }
      } catch (error) {
        console.error('Error fetching poll template:', error);
        setError('Failed to fetch poll template. Please try again later.');
      }
    };
    if (selectedPoll && (selectedPoll.id !== 'select_a_poll')) {
      fetchPollTemplate(selectedPoll.id);
    } else {
      setPollTemplate(null);
      setReportDataMap(null);
    }
  }, [selectedPoll]);

  useEffect(() => {
    const fetchPolls = async () => {
      try {
        const pollsCollection = collection(db, 'polls');
        const q = query(pollsCollection, where('archived', '==', false)); // Filter out archived polls
        const pollsSnapshot = await getDocs(q);
        const pollsData = [
          {
            id: 'select_a_poll',
            title: 'None selected',
          } as Poll,
          ...pollsSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() } as Poll))
        ];
        setPolls(pollsData);
        setSelectedPoll(pollsData[0]);
      } catch (error) {
        console.error("Error fetching polls:", error);
      }
    };

    fetchPolls();
  }, []);

  const renderQuestionReport = (reportData: ReportData) => {
    switch (reportData.question.type) {
      case 'select':
        return <SelectPollQuestionReport reportData={reportData} />;
      case 'multiselect':
        return <MultiselectPollQuestionReport reportData={reportData} />;
      case 'range':
        return <RangePollQuestionReport reportData={reportData} />;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!user?.permissions.view_reports) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Typography variant="h6" color="error">
          You do not have permission to access the Reports page.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4">Survey Report</Typography>
        <Box>
          <Select
            value={selectedPoll?.id ?? ''}
            onChange={(e) => {
              const selectedPollId = e.target.value;
              const foundPoll = polls.find((poll) => poll.id === selectedPollId);
              setSelectedPoll(foundPoll || null);
            }}
          >
            {polls.map((poll) => (
              <MenuItem key={poll.id} value={poll.id}>
                {poll.title}
              </MenuItem>
            ))}
          </Select>
        </Box>
      </Box>
      
      {error && <Typography color="error">{error}</Typography>}

      {isAnalyzing && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
          <CircularProgress />
      </Box>
      )}

      {!pollTemplate && !reportDataMap && (
        <Typography variant="h6">Please select a poll</Typography>
      )}

      {!isAnalyzing && reportDataMap && (
        <Box mb={2}>
          <Typography variant="h6">Respondents: {responseCount}</Typography>
        </Box>
      )}

      {!isAnalyzing && reportDataMap && Object.entries(reportDataMap).map(([questionId, reportData]) => (
        <Box key={questionId} mb={2}>
          {renderQuestionReport(reportData)}
        </Box>
      ))}
      {!isAnalyzing && textQnAs && <TextPollQuestionReport textQnAs={textQnAs} />}
    </Box>
  );
};

export default SurveyReport;
